import { call, select, put, all } from "redux-saga/effects";
import { handleSagaError } from "@/sagas/index";
import { actions as tvActions } from "@/reducers/tv";
import { checkMocks } from "@/utils/commonUtils";
import { normalizeTvClientInfo } from "./normalizers/tvNormalizer";
import { FUSION, TRIO, DUO } from "@/constants/constants";
import { getProductsByProductId } from "@/containers/my-products/utils";
import { getZonaToName } from "@/utils/tvUtils";
import { TEXT_TYPES_CODES } from "@/constants/catalogue";
import sendGaEvent from "@gcloud-npm/utils.metrics.send-ga-component-event";

// Getting info from state
export const getTVClientInfoFromState = (state) => state.tv.clientTVInfo;
const contextStore = (state) => state.main.context;
const hiredProductsListStore = (state) => state.product.hiredProductsList;
const zoneStore = (state) => state.main.zone;
const pikolinStore = (state) => state.user.pikolin;

export function* getClientTvInfo({ handlingCode = 0, productId }) {
  try {
    const MOCK_INFO = "getClientTvInfo";
    const context = yield select(contextStore);
    let tvClientInfo = yield select(getTVClientInfoFromState);
    tvClientInfo = tvClientInfo || [];
    const currentTVbundle = tvClientInfo.find(
      (currentClientTVInfo) => currentClientTVInfo.productId === productId
    );
    if (!currentTVbundle) {
      let response = checkMocks(MOCK_INFO);
      // yield put(tvActions.getClientTvInfo(productId));
      if (response === null) {
        let getTvInfoModule = yield call(() =>
          import("@/adabyron/telcol/tv/get-tv-info")
        );
        let getTvInfo = getTvInfoModule.getTvInfo;

        const data = { productId };
        response = yield call(getTvInfo, {
          data,
          context,
        });
      }
      tvClientInfo = normalizeTvClientInfo(response.datos, productId);
      // we give back data to reducer
      yield put(tvActions.successGetTvClientInfo(tvClientInfo));
    }

    return {
      status: "ok",
    };
  } catch (error) {
    const reducerFailureAction = tvActions.failureGetTvClientInfo(true);
    yield handleSagaError(
      "getClientTvInfo",
      error,
      "ERROR",
      handlingCode,
      reducerFailureAction
    );
    return {
      status: "ko",
      error,
    };
  }
}

export function* getMovistarLiteStatus() {
  try {
    const context = yield select(contextStore);
    let getMovistarListeStatusModule = yield call(() =>
      import("@/adabyron/telcol/tv/get-movistar-lite-status")
    );
    let getMovistarLiteStatusPromise =
      getMovistarListeStatusModule.getMovistarLiteStatus;
    const response = yield call(getMovistarLiteStatusPromise, { context });
    yield put(tvActions.successGetMovistarLiteStatus(response?.datos));
  } catch (err) {
    const reducerFailureAction = tvActions.failureGetMovistarLiteStatus(true);
    yield handleSagaError(err, reducerFailureAction);
  }
}

export function* getMovistarLiteActivation({ handlingCode = 0, payload }) {
  try {
    const context = yield select(contextStore);
    let getMovistarLiteActivationModule = yield call(() =>
      import("@/adabyron/telcol/tv/get-movistar-lite-activation")
    );
    let getMovistarLiteActivationPromise =
      getMovistarLiteActivationModule.getMovistarLiteActivation;
    const response = yield call(getMovistarLiteActivationPromise, {
      data: { productId: payload },
      context,
    });
    yield put(tvActions.successGetMovistarLiteActivation(response?.datos));
  } catch (err) {
    const reducerFailureAction =
      tvActions.failureGetMovistarLiteActivation(err);
    yield handleSagaError(err, reducerFailureAction);
  }
}

const prepareProductOfferingsForDisgregar = (
  zone,
  hiredProductsList,
  mainTvProduct,
  parentMainTvProduct
) => {
  const businessZoneId = getZonaToName(zone);

  const productOfferings = [];

  productOfferings.push({
    id: parentMainTvProduct.poId,
    businessZoneId: businessZoneId,
    promotions: [],
  });

  mainTvProduct.productosContratadosDedependientesProductId.forEach((elem) => {
    const productoContratadoDependienteFinded = getProductsByProductId(
      hiredProductsList,
      elem
    );
    if (productoContratadoDependienteFinded?.bundleId === "498") {
      const netflixProductCharacteristics =
        productoContratadoDependienteFinded.characteristics?.[0];
      productOfferings.push({
        id: productoContratadoDependienteFinded.bundleId,
        businessZoneId: businessZoneId,
        promotions: [],
        characteristics: [
          {
            id: parseInt(netflixProductCharacteristics.id, 10),
            value: netflixProductCharacteristics.valueId,
          },
        ],
      });
    }
  });

  return productOfferings;
};

const addProductosContratadosDependientes = (
  mainTvProduct,
  hiredProductsList
) => {
  const tvPackagesArray = [];
  const defaultRules = { promoCode: null, tipoCliente: "cliente" };
  mainTvProduct.productosContratadosDedependientesProductId.forEach((elem) => {
    let productOffering = null;
    const productoContratadoDependienteFinded = getProductsByProductId(
      hiredProductsList,
      elem
    );
    if (productoContratadoDependienteFinded?.bundleId === "498") {
      const netflixProductCharacteristics =
        productoContratadoDependienteFinded.characteristics?.[0];
      productOffering = `${productoContratadoDependienteFinded.bundleId}_${netflixProductCharacteristics.valueId}`;
    } else {
      productOffering = productoContratadoDependienteFinded.poId;
    }
    tvPackagesArray.push({
      productOffering: productOffering,
      rules: defaultRules,
    });
  });

  return tvPackagesArray;
};

const isDuplicate = (obj, index, self) => {
  return (
    index === self.findIndex((t) => t.productOffering === obj.productOffering)
  );
};

export function* getTvPackagesInfo({ handlingCode = 0, payload }) {
  try {
    const [context, hiredProductsList, pikolin, zone] = yield all([
      select(contextStore),
      select(hiredProductsListStore),
      select(pikolinStore),
      select(zoneStore),
    ]);
    const { productId } = payload;
    const mainTvProduct = getProductsByProductId(hiredProductsList, productId);
    const parentMainTvProduct = getProductsByProductId(
      hiredProductsList,
      mainTvProduct.parentProductId
    );
    //Check if the tv parent it is a fusion or miMovistar
    let response = null;
    let tvPackagesArray = [];
    if (parentMainTvProduct) {
      if (parentMainTvProduct.productType === FUSION) {
        if (pikolin) {
          const productOfferings = prepareProductOfferingsForDisgregar(
            zone,
            hiredProductsList,
            mainTvProduct,
            parentMainTvProduct
          );

          const disgregarModule = yield call(() =>
            import("@/adabyron/telcol/tv/disgregar")
          );
          const { disAggregateModules } = disgregarModule;
          response = yield call(disAggregateModules, {
            context,
            entry: { productOfferings },
          });

          const disgregarPackages = response.datos.modules
            .filter(
              (tvPackage) =>
                tvPackage.category === "Movistar Plus+" ||
                tvPackage.category === "Movistar Plus"
            )
            .map((obj) => obj.id);

          if (
            disgregarPackages &&
            Array.isArray(disgregarPackages) &&
            disgregarPackages.length > 0
          ) {
            disgregarPackages.forEach((elem) => {
              tvPackagesArray.push({
                productOffering: elem,
                rules: { promoCode: null, tipoCliente: "cliente" },
              });
            });
          } else {
            tvPackagesArray.push({
              productOffering: mainTvProduct.poId,
              rules: { promoCode: null, tipoCliente: "cliente" },
            });
          }

          tvPackagesArray.push(
            ...addProductosContratadosDependientes(
              mainTvProduct,
              hiredProductsList
            )
          );
        } else {
          tvPackagesArray.push({
            productOffering: mainTvProduct.poId,
            rules: { promoCode: null, tipoCliente: "cliente" },
          });
          tvPackagesArray.push(
            ...addProductosContratadosDependientes(
              mainTvProduct,
              hiredProductsList
            )
          );
        }
      }
      if (parentMainTvProduct.productType === TRIO) {
        tvPackagesArray.push({
          productOffering: mainTvProduct.poId,
          rules: { promoCode: null, tipoCliente: "cliente" },
        });

        tvPackagesArray.push(
          ...addProductosContratadosDependientes(
            mainTvProduct,
            hiredProductsList
          )
        );
      }
    } else {
      tvPackagesArray.push({
        productOffering: mainTvProduct.poId,
        rules: { promoCode: null, tipoCliente: "cliente" },
      });
    }

    tvPackagesArray = tvPackagesArray.filter(isDuplicate);

    //Call Orion
    const getOrionInfoModule = yield call(() =>
      import("@gcloud-npm/adabyron.orion.get-by-alias")
    );
    const { getByAlias } = getOrionInfoModule;
    const build = false;
    response = yield call(getByAlias, {
      context,
      build,
      path: "products",
      alias: "list",
      conditionList: JSON.stringify(tvPackagesArray),
      forcedEnv: build ? null : "qa",
    });

    const sortedResponse = [];
    for (const tvPackage of tvPackagesArray) {
      if (response.responseMap.hasOwnProperty(tvPackage.productOffering)) {
        sendGaEvent({
          component_name: "api_call",
          api_detail: "orion-standard-api-products-list-found",
          product_id: tvPackage.productOffering,
          api_status: "get",
          api_result:
            (response?.responseMap && Object.keys(response.responseMap)) || [],
        });
        const foundProduct = response.responseMap[tvPackage.productOffering];
        let packageData = null;
        if (
          foundProduct?.data?.fields?.CARACTERISTICAS_CARD?.fields
            ?.LISTADO_CARACTERISTICAS_CARD[0]?.fields
        ) {
          packageData =
            foundProduct.data.fields.CARACTERISTICAS_CARD.fields
              .LISTADO_CARACTERISTICAS_CARD[0].fields;
          sortedResponse.push(packageData);
        }
        if (
          !packageData &&
          foundProduct?.data?.fields?.PRODUCT_AREA_PRIVADA_CARACTERISTICS
            ?.fields
        ) {
          sortedResponse.push(
            foundProduct.data.fields.PRODUCT_AREA_PRIVADA_CARACTERISTICS.fields
          );
        }
      } else {
        sendGaEvent({
          component_name: "api_call",
          api_detail: "orion-standard-api-products-list-not-found",
          product_id: tvPackage.productOffering,
          api_status: "get",
          api_result:
            (response?.responseMap && Object.keys(response.responseMap)) || [],
        });
      }
    }

    let result = null;
    if (Array.isArray(sortedResponse) && sortedResponse.length > 0) {
      result = sortedResponse;
    } else {
      const getProductOfferingModule = yield call(() =>
        import("@/adabyron/telcol/catalogo/oferta")
      );

      const { getProductOffering } = getProductOfferingModule;
      const data = { idPO: mainTvProduct.poId };
      const getProductOfferingResponse = yield call(getProductOffering, {
        data,
        context,
      });

      const texts = getProductOfferingResponse.datos.texts;
      result = [
        {
          icon: "tv",
          title:
            texts.find((text) => text.typeId === TEXT_TYPES_CODES.ORDINARY)
              .text || "Paquete Televisión Movistar",
          description: texts.find(
            (text) => text.typeId === TEXT_TYPES_CODES.BRIEF
          ).text,
        },
      ];
    }

    yield put(
      tvActions.succesGetTvPackagesInfo({
        data: result,
        productId: productId,
      })
    );
  } catch (error) {
    const reducerFailureAction = tvActions.failureGetTvPackagesInfo(true);
    yield handleSagaError(
      "getTvPackagesInfo",
      error,
      "ERROR",
      handlingCode,
      reducerFailureAction
    );
    return {
      status: "ko",
      error,
    };
  }
}
